// src/parametres/Utilisateurs.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Button, Spinner, Form,
    Modal, InputGroup, Tooltip, OverlayTrigger, Pagination
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Utilisateurs() {
    const navigate = useNavigate();
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 🔎 Recherche & filtres
    const [filterText, setFilterText] = useState('');
    const [filtreRole, setFiltreRole] = useState('tous');
    const [filtreStatut, setFiltreStatut] = useState('tous');

    // Modal édition
    const [showModal, setShowModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({
        nom_utilisateur: '',
        prenom_utilisateur: '',
        email: '',
        role: 'commercial',
        telephone: '',
        actif: true
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Suppression
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => { fetchUtilisateurs(); }, []);

    // Reset pagination si filtres changent
    useEffect(() => { setCurrentPage(1); }, [filterText, filtreRole, filtreStatut, itemsPerPage]);

    /* ============================================================
       FETCH — utilise utilisateurs.php?action=list
       ============================================================ */
    const fetchUtilisateurs = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/utilisateurs.php?action=list&all=1`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Erreur chargement");
            }

            const result = await res.json();
            setUtilisateurs(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       FILTRAGE
       ============================================================ */
    const filteredUtilisateurs = useMemo(() => {
        const search = filterText.trim().toLowerCase();
        return utilisateurs.filter(u => {
            const matchText = !search ||
                (u.nom_utilisateur || '').toLowerCase().includes(search) ||
                (u.prenom_utilisateur || '').toLowerCase().includes(search) ||
                (u.email || '').toLowerCase().includes(search);

            const matchRole = filtreRole === 'tous' || u.role === filtreRole;

            const matchStatut = filtreStatut === 'tous'
                || (filtreStatut === 'actif' && (u.actif === 1 || u.actif === true))
                || (filtreStatut === 'inactif' && (u.actif === 0 || u.actif === false));

            return matchText && matchRole && matchStatut;
        });
    }, [utilisateurs, filterText, filtreRole, filtreStatut]);

    /* ============================================================
       PAGINATION
       ============================================================ */
    const totalPages = Math.max(1, Math.ceil(filteredUtilisateurs.length / itemsPerPage));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredUtilisateurs.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(Math.min(Math.max(1, page), totalPages));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="s" disabled />);
        }
        for (let n = startPage; n <= endPage; n++) {
            items.push(
                <Pagination.Item key={n} active={n === currentPage} onClick={() => handlePageChange(n)}>
                    {n}
                </Pagination.Item>
            );
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="e" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }
        return items;
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    // ✅ Nouveau → redirige vers la page dédiée
    const handleAdd = () => {
        navigate('/parametres/utilisateurs/nouveau?returnTo=/parametres/utilisateurs');
    };

    // ✅ Modifier → garde la modale d'édition rapide
    const handleEdit = (user) => {
        setCurrentUser(user);
        setFormData({
            nom_utilisateur: user.nom_utilisateur,
            prenom_utilisateur: user.prenom_utilisateur,
            email: user.email,
            role: user.role,
            telephone: user.telephone || '',
            actif: user.actif === 1 || user.actif === true
        });
        setFormError('');
        setShowModal(true);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        setIsDeleting(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/utilisateurs.php?id=${userToDelete.id_utilisateur}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();

            if (result.success) {
                setShowDeleteModal(false);
                setUserToDelete(null);
                showToast('success', 'Supprimé', "Utilisateur supprimé avec succès");
                fetchUtilisateurs();
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    /* ============================================================
       MISE À JOUR (modale)
       ============================================================ */
    const handleSubmit = async () => {
        if (!formData.nom_utilisateur || !formData.prenom_utilisateur || !formData.email) {
            setFormError("Veuillez remplir tous les champs obligatoires");
            return;
        }

        setSaving(true);
        setFormError('');

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/utilisateurs.php?id=${currentUser.id_utilisateur}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    nom_utilisateur: formData.nom_utilisateur,
                    prenom_utilisateur: formData.prenom_utilisateur,
                    email: formData.email,
                    role: formData.role,
                    telephone: formData.telephone,
                    actif: formData.actif
                })
            });
            const result = await res.json();

            if (result.success) {
                setShowModal(false);
                showToast('success', 'Modifié', "Utilisateur modifié avec succès");
                fetchUtilisateurs();
            } else {
                setFormError(result.message || "Erreur lors de la modification");
            }
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getRoleBadge = (role) => {
        const configs = {
            admin:        { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Administrateur', icon: 'ri-shield-star-line' },
            gestionnaire: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Gestionnaire',   icon: 'ri-briefcase-line' },
            commercial:   { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', label: 'Commercial',     icon: 'ri-user-star-line' },
            magasinier:   { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Magasinier',     icon: 'ri-box-3-line' }
        };
        const config = configs[role] || configs.commercial;
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i> {config.label}
            </span>
        );
    };

    const getStatutBadge = (actif) => (
        <span className="status-badge-charte" style={{
            background: actif
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : 'linear-gradient(135deg, #64748b, #475569)'
        }}>
            <i className={actif ? 'ri-check-line' : 'ri-close-line'}></i>
            {actif ? 'Actif' : 'Inactif'}
        </span>
    );

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner animation="border" style={{ width: '3rem', height: '3rem', color: COLORS.primary }} />
                            <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des utilisateurs...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper"><i className="ri-user-settings-line"></i></div>
                            <div>
                                <h5 className="header-title">Utilisateurs</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-group-line"></i> {filteredUtilisateurs.length} utilisateur(s)
                                    </span>
                                    {utilisateurs.filter(u => u.actif).length > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-check-line"></i> {utilisateurs.filter(u => u.actif).length} actif(s)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchUtilisateurs} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={handleAdd}>
                                <i className="ri-add-line"></i> Nouvel utilisateur
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/parametres')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Nom, prénom, email..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select value={filtreRole} onChange={(e) => setFiltreRole(e.target.value)} className="filter-select-charte">
                                        <option value="tous">Tous les rôles</option>
                                        <option value="admin">Administrateur</option>
                                        <option value="gestionnaire">Gestionnaire</option>
                                        <option value="commercial">Commercial</option>
                                        <option value="magasinier">Magasinier</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2">
                                    <Form.Select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)} className="filter-select-charte">
                                        <option value="tous">Tous statuts</option>
                                        <option value="actif">Actifs</option>
                                        <option value="inactif">Inactifs</option>
                                    </Form.Select>
                                </Col>
                                <Col md="1">
                                    <Form.Select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="filter-select-charte">
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i> {filteredUtilisateurs.length}
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex align-items-center gap-2">
                                <i className="ri-group-line text-white fs-5"></i>
                                <span className="fw-semibold text-white">Liste des utilisateurs</span>
                                <span className="badge-header-light">{filteredUtilisateurs.length}</span>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Utilisateur</th>
                                            <th>Email</th>
                                            <th className="text-center">Rôle</th>
                                            <th className="text-center">Statut</th>
                                            <th>Dernière connexion</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((user) => (
                                            <tr key={user.id_utilisateur}>
                                                <td className="ps-4" style={{ minWidth: '250px' }}>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="avatar-icon-charte"><i className="ri-user-3-line"></i></div>
                                                        <div>
                                                            <strong>{user.prenom_utilisateur} {user.nom_utilisateur}</strong>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="d-flex align-items-center gap-1 text-muted">
                                                        <i className="ri-mail-line"></i>
                                                        {user.email}
                                                    </span>
                                                </td>
                                                <td className="text-center">{getRoleBadge(user.role)}</td>
                                                <td className="text-center">{getStatutBadge(user.actif)}</td>
                                                <td>
                                                    {user.derniere_connexion ? (
                                                        <span className="d-flex align-items-center gap-1 text-muted">
                                                            <i className="ri-calendar-line"></i> {formatDate(user.derniere_connexion)}
                                                        </span>
                                                    ) : <span className="text-muted">—</span>}
                                                </td>
                                                <td className="text-center pe-4">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                                                            <button className="action-btn-charte" onClick={() => handleEdit(user)} style={{ '--btn-color': COLORS.primary }}>
                                                                <i className="ri-pencil-line"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                                            <button className="action-btn-charte" onClick={() => handleDeleteClick(user)} style={{ '--btn-color': COLORS.danger }}>
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="6">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte"><i className="ri-user-line"></i></div>
                                                        <h6 className="fw-semibold mb-1">
                                                            {filterText || filtreRole !== 'tous' || filtreStatut !== 'tous'
                                                                ? 'Aucun résultat'
                                                                : 'Aucun utilisateur trouvé'}
                                                        </h6>
                                                        <p className="text-muted small mb-3">
                                                            {filterText || filtreRole !== 'tous' || filtreStatut !== 'tous'
                                                                ? 'Aucun utilisateur ne correspond à vos critères.'
                                                                : 'Commencez par créer un nouvel utilisateur.'}
                                                        </p>
                                                        {!(filterText || filtreRole !== 'tous' || filtreStatut !== 'tous') && (
                                                            <Button className="btn-modern-primary" onClick={handleAdd}>
                                                                <i className="ri-add-line me-1"></i> Nouvel utilisateur
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredUtilisateurs.length)}</strong> sur <strong>{filteredUtilisateurs.length}</strong> utilisateur(s)
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                {renderPagination()}
                                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </div>

                {/* ===== MODAL MODIFICATION ===== */}
                <Modal show={showModal} onHide={() => !saving && setShowModal(false)} centered backdrop={saving ? 'static' : true}>
                    <Modal.Header closeButton={!saving} className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-pencil-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Modifier l'utilisateur</Modal.Title>
                                <p className="text-white-50 mb-0 small">Modifiez les informations du compte</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {formError && (
                            <div className="alert-error-charte mb-3">
                                <div className="d-flex align-items-center">
                                    <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                                    <div className="ms-3">
                                        <strong>Erreur</strong>
                                        <p className="mb-0 small">{formError}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Form>
                            <Row className="g-3">
                                <Col md="6">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">Prénom <span className="text-danger">*</span></Form.Label>
                                        <InputGroup className="input-modern">
                                            <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                            <Form.Control type="text" name="prenom_utilisateur" value={formData.prenom_utilisateur} onChange={handleChange} required disabled={saving} />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md="6">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern">Nom <span className="text-danger">*</span></Form.Label>
                                        <InputGroup className="input-modern">
                                            <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                            <Form.Control type="text" name="nom_utilisateur" value={formData.nom_utilisateur} onChange={handleChange} required disabled={saving} />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mt-3">
                                <Form.Label className="form-label-modern">Email <span className="text-danger">*</span></Form.Label>
                                <InputGroup className="input-modern">
                                    <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                    <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required disabled={saving} />
                                </InputGroup>
                            </Form.Group>

                            <Form.Group className="mt-3">
                                <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                <InputGroup className="input-modern">
                                    <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                    <Form.Control type="tel" name="telephone" value={formData.telephone} onChange={handleChange} disabled={saving} />
                                </InputGroup>
                            </Form.Group>

                            <Form.Group className="mt-3">
                                <Form.Label className="form-label-modern">Rôle</Form.Label>
                                <InputGroup className="input-modern">
                                    <InputGroup.Text><i className="ri-shield-user-line"></i></InputGroup.Text>
                                    <Form.Select name="role" value={formData.role} onChange={handleChange} disabled={saving}>
                                        <option value="admin">Administrateur</option>
                                        <option value="gestionnaire">Gestionnaire</option>
                                        <option value="commercial">Commercial</option>
                                        <option value="magasinier">Magasinier</option>
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>

                            <Form.Group className="mt-3">
                                <Form.Check type="switch" id="actif" name="actif" label="Compte actif" checked={formData.actif} onChange={handleChange} className="switch-charte fw-semibold" disabled={saving} />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowModal(false)} disabled={saving}>Annuler</Button>
                        <Button className="btn-modern-primary" onClick={handleSubmit} disabled={saving}>
                            {saving ? <><Spinner size="sm" className="me-2" />Traitement...</> : <><i className="ri-check-line me-2"></i>Modifier</>}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ===== MODAL SUPPRESSION ===== */}
                <Modal show={showDeleteModal} onHide={() => !isDeleting && setShowDeleteModal(false)} centered backdrop={isDeleting ? 'static' : true}>
                    <Modal.Header closeButton={!isDeleting} className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                                <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <p className="text-center text-muted mb-3">
                            Êtes-vous sûr de vouloir supprimer{' '}
                            <strong style={{ color: COLORS.primary }}>
                                {userToDelete?.prenom_utilisateur} {userToDelete?.nom_utilisateur}
                            </strong> ?
                        </p>

                        <div className="summary-card-charte mb-3">
                            <div className="d-flex align-items-center gap-3">
                                <div className="user-avatar-charte"><i className="ri-user-3-line"></i></div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                        {userToDelete?.prenom_utilisateur} {userToDelete?.nom_utilisateur}
                                    </div>
                                    <div className="text-muted small"><i className="ri-mail-line me-1"></i>{userToDelete?.email}</div>
                                    <div className="mt-2">{userToDelete && getRoleBadge(userToDelete.role)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="alert-error-charte mb-0">
                            <div className="d-flex align-items-center">
                                <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                <div className="ms-3">
                                    <strong>Attention</strong>
                                    <p className="mb-0 small">La suppression entraînera la perte définitive des accès de cet utilisateur</p>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>Annuler</Button>
                        <Button className="btn-modern-danger" onClick={handleConfirmDelete} disabled={isDeleting}>
                            {isDeleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }
                .header-modern { background: white; padding: 1.2rem 1.8rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid var(--secondary); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, var(--primary), var(--secondary)); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25); }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern { background: #f1f4f9; color: #475569; padding: 0.3rem 0.8rem; border-radius: 50px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                .filter-card-charte { background: linear-gradient(135deg, var(--primary-dark), var(--primary)); border-radius: 12px; border: none; box-shadow: 0 8px 24px rgba(41,90,167,0.15); }
                .search-box-charte { position: relative; }
                .search-icon-charte { position: absolute; top: 50%; left: 1rem; transform: translateY(-50%); color: var(--primary); z-index: 2; pointer-events: none; }
                .search-input-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem 0.6rem 2.75rem !important; font-size: 0.9rem; }
                .filter-select-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem !important; font-size: 0.85rem; cursor: pointer; }
                .count-badge-charte { background: rgba(255,255,255,0.95); color: var(--primary); padding: 0.6rem 1.25rem; border-radius: 50px; font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }

                .card-header-gradient { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%); border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; padding: 1rem 1.5rem; border: none; }
                .badge-header-light { background: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.3); border-radius: 50px; padding: 0.25rem 0.75rem; font-size: 0.75rem; font-weight: 600; }

                .table-charte thead th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 0.9rem 0.75rem; }
                .table-charte tbody td { padding: 0.9rem 0.75rem; vertical-align: middle; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .status-badge-charte { display: inline-flex; align-items: center; gap: 4px; color: white; padding: 0.3rem 0.75rem; border-radius: 50px; font-size: 0.7rem; font-weight: 600; white-space: nowrap; }
                .avatar-icon-charte { width: 40px; height: 40px; background: ${COLORS.primary}15; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--primary); flex-shrink: 0; font-size: 1.2rem; }

                .action-btn-charte { width: 36px; height: 36px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: white; border: 1.5px solid #e2e8f0; color: #64748b; font-size: 1rem; transition: all 0.2s ease; cursor: pointer; }
                .action-btn-charte:hover { border-color: var(--btn-color); color: var(--btn-color); transform: translateY(-2px); box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15); }

                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte { width: 72px; height: 72px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8; }

                .pagination-wrapper-charte { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; background: white; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); flex-wrap: wrap; gap: 1rem; }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link { border-radius: 10px; border: 1.5px solid #e2e8f0; color: #1e293b; padding: 0.5rem 0.9rem; font-weight: 500; font-size: 0.85rem; transition: all 0.2s; }
                .pagination-charte .page-item.active .page-link { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border-color: var(--primary); color: white; box-shadow: 0 4px 12px rgba(41,90,167,0.3); }
                .pagination-charte .page-item .page-link:hover { background: #f1f5f9; border-color: var(--secondary); color: var(--primary); }

                .form-label-modern { font-weight: 600; color: #334155; font-size: 0.85rem; margin-bottom: 0.4rem; }
                .input-modern .input-group-text { background: #f8fafc; border: 2px solid #e2e8f0; border-right: none; color: var(--primary); border-radius: 10px 0 0 10px; }
                .input-modern .form-control, .input-modern .form-select { border: 2px solid #e2e8f0; border-left: none; border-radius: 0 10px 10px 0; padding: 0.6rem 0.9rem; font-size: 0.9rem; }
                .switch-charte .form-check-input { width: 2.5em; height: 1.25em; cursor: pointer; background-color: #cbd5e1; border-color: #cbd5e1; }
                .switch-charte .form-check-input:checked { background-color: var(--secondary); border-color: var(--secondary); }

                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon { width: 48px; height: 48px; border-radius: 12px; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-size: 1.5rem; color: white; }
                .summary-card-charte { background: linear-gradient(135deg, #f8fafc, #ffffff); padding: 1rem 1.25rem; border-radius: 12px; border: 1px solid #e2e8f0; }
                .user-avatar-charte { width: 52px; height: 52px; border-radius: 12px; background: linear-gradient(135deg, var(--primary), var(--primary-dark)); display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; font-size: 1.2rem; flex-shrink: 0; }
                .alert-error-charte { border: none; background: #fef2f2; border-left: 4px solid var(--danger); border-radius: 16px; padding: 1rem 1.5rem; }
                .alert-error-icon { width: 48px; height: 48px; border-radius: 12px; background: rgba(239,68,68,0.1); display: flex; align-items: center; justify-content: center; color: var(--danger); font-size: 1.5rem; }

                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; font-size: 0.85rem; font-weight: 500; color: #4b5563; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-outline:hover { border-color: var(--secondary); color: var(--secondary); background: #f0fbff; }
                .btn-modern-primary { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; font-size: 0.85rem; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-danger { background: linear-gradient(135deg, var(--danger), #dc2626); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; font-size: 0.85rem; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-outline:disabled, .btn-modern-primary:disabled, .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-progress { height: 3px; animation: shrink linear forwards; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }

                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}